import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CurrencyInput from 'react-currency-input-field';

import { editarEvento, obtenerEvento, limpiarEvento } from '../../actions/eventos';
import { buscarMetodosTipo } from '../../actions/metodos';
import { editarBreadcrumb } from '../../actions/breadcrumb';
import { setAlert } from '../../actions/alert';
import { CustomValueContainer } from '../../components/react_select/ReactSelect';

const styles_select = {
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    container: (provided, state) => ({
        ...provided,
    }),
    control: (provided, state) => ({
        ...provided,
        paddingTop: '5px',
        paddingBottom: '5px',
    }),
    valueContainer: (provided, state) => ({
        ...provided,
        overflow: "visible",
        marginTop: "10px"
    }),
    placeholder: (provided, state) => ({
        ...provided,
        position: "absolute",
        top: state.hasValue || state.selectProps.inputValue ? -15 : "0%",
        transition: "top 0.1s, font-size 0.1s",
        fontSize: (state.hasValue || state.selectProps.inputValue) && 13
    })
};

const opcionesCobrado = [
    { value: true, label: 'Sí' },
    { value: false, label: 'No' }
];

const EditarEvento = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const detalles = useSelector(state => state.eventos.detalles);
    const metodosListado = useSelector(state => state.metodos.listado);

    const [eventoData, setEventoData] = useState({
        id: null,
        metodo: null,
        nombre: null,
        total: null,
        fecha_inicio: null,
        fecha_fin: null,
        cobrado: null
    });

    const [metodoData, setMetodoData] = useState(null);
    const [cobradoData, setCobradoData] = useState(null);
    const [statusProcesar, setStatusProcesar] = useState(false);

    useEffect(() => {
        dispatch(buscarMetodosTipo(4));

        return () => {
            dispatch(limpiarEvento());
        };
    }, []);

    useEffect(() => {
        if (id) {
            dispatch(obtenerEvento(id));
            dispatch(editarBreadcrumb([
                { nombre: 'Eventos', path: '/eventos', activo: false },
                { nombre: 'Editar evento', path: `/eventos/editar/${id}`, activo: true }
            ]));
        }
    }, [id]);

    useEffect(() => {
        if (detalles != null) {
            setEventoData({
                id: detalles._id,
                metodo: detalles.metodo?._id ?? null,
                nombre: detalles.nombre ?? null,
                total: detalles.total ?? null,
                fecha_inicio: detalles.fecha_inicio ? new Date(detalles.fecha_inicio) : null,
                fecha_fin: detalles.fecha_fin ? new Date(detalles.fecha_fin) : null,
                cobrado: detalles.cobrado ?? null
            });

            if (detalles.metodo) {
                const metodoEncontrado = metodosListado.find(m => m._id === detalles.metodo._id);
                setMetodoData(metodoEncontrado
                    ? { value: metodoEncontrado._id, label: metodoEncontrado.nombre }
                    : { value: detalles.metodo._id, label: detalles.metodo.nombre }
                );
            }

            const cobradoEncontrado = opcionesCobrado.find(o => o.value === detalles.cobrado);
            setCobradoData(cobradoEncontrado || null);
        }
    }, [detalles]);

    const handleInputChange = ({ target }) => {
        setEventoData({ ...eventoData, [target.name]: target.value });
    };

    const handleSelectMetodo = (option) => {
        setMetodoData(option);
        setEventoData({ ...eventoData, metodo: option?.value ?? null });
    };

    const handleSelectCobrado = (option) => {
        setCobradoData(option);
        setEventoData({ ...eventoData, cobrado: option?.value ?? null });
    };

    const handleDatePicker = (name, value) => {
        if (value) {
            if (name === 'fecha_inicio') {
                value.setHours(0, 0, 0, 0);
            } else if (name === 'fecha_fin') {
                value.setHours(23, 59, 59, 999);
            }
        }
        setEventoData({ ...eventoData, [name]: value });
    };

    const handleNumberChange = (value, name) => {
        setEventoData({ ...eventoData, [name]: value || '' });
    };

    const formatDateLocal = (date) => {
        const pad = (n) => n.toString().padStart(2, '0');

        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} `
            + `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    const handleSubmit = async () => {
        if (!eventoData.fecha_inicio) {
            dispatch(setAlert('La fecha de inicio es requerida', 'danger'));
            return;
        }
        if (!eventoData.fecha_fin) {
            dispatch(setAlert('La fecha de fin es requerida', 'danger'));
            return;
        }
        if (eventoData.fecha_fin < eventoData.fecha_inicio) {
            dispatch(setAlert('La fecha de fin no puede ser anterior a la fecha de inicio', 'danger'));
            return;
        }

        const payload = {
            ...eventoData,
            fecha_inicio: formatDateLocal(eventoData.fecha_inicio),
            fecha_fin: formatDateLocal(eventoData.fecha_fin)
        };

        setStatusProcesar(true);
        await dispatch(editarEvento(payload));
        setStatusProcesar(false);
        navigate('/eventos');
    };

    const metodosOpciones = metodosListado.map(m => ({ value: m._id, label: m.nombre }));

    return (
        <div className="row mt-5">
            <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
                <h4 className="mb-4">Editar evento</h4>
            </div>

            <div className="col-12">
                <div className="accordion" id="accordionEditarEvento">
                    <div className="accordion-item">
                        <h2 className="accordion-header" id="headingInfo">
                            <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseInfo" aria-expanded="true" aria-controls="collapseInfo">
                                Información del evento
                            </button>
                        </h2>
                        <div id="collapseInfo" className="accordion-collapse collapse show" aria-labelledby="headingInfo" data-bs-parent="#accordionEditarEvento">
                            <div className="accordion-body">
                                <div className="row">
                                    <div className="col-md-3 form-group mb-3">
                                        <div className="input-group h-100">
                                            <Select
                                                name="metodo"
                                                className="select-group"
                                                classNamePrefix="react-select"
                                                placeholder="Método de pago"
                                                options={metodosOpciones}
                                                value={metodoData}
                                                onChange={handleSelectMetodo}
                                                menuPortalTarget={document.body}
                                                components={{ ValueContainer: CustomValueContainer }}
                                                styles={{ ...styles_select }}
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-3 mb-3">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Nombre"
                                                id="nombre"
                                                name="nombre"
                                                value={eventoData.nombre || ''}
                                                onChange={handleInputChange}
                                            />
                                            <label htmlFor="nombre">Nombre</label>
                                        </div>
                                    </div>
                                    <div className="col-md-3 mb-3">
                                        <div className="form-floating">
                                            <CurrencyInput
                                                className="form-control"
                                                placeholder="Costo del evento"
                                                id="total"
                                                name="total"
                                                value={eventoData.total || ''}
                                                onValueChange={(value, name) => handleNumberChange(value, name)}
                                                prefix="$"
                                                decimalsLimit={2}
                                                groupSeparator=","
                                                decimalSeparator="."
                                            />
                                            <label htmlFor="total">Costo del evento</label>
                                        </div>
                                    </div>
                                    <div className="col-md-3 form-group mb-3">
                                        <div className="input-group h-100">
                                            <Select
                                                name="cobrado"
                                                className="select-group"
                                                classNamePrefix="react-select"
                                                placeholder="¿Las ventas se deben cobrar?"
                                                options={opcionesCobrado}
                                                value={cobradoData}
                                                onChange={handleSelectCobrado}
                                                menuPortalTarget={document.body}
                                                components={{ ValueContainer: CustomValueContainer }}
                                                styles={{ ...styles_select }}
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-3 form-group mb-3">
                                        <div className="input-group">
                                            <div className="form-floating">
                                                <DatePicker
                                                    className="form-control text-center input-datepicker"
                                                    selected={eventoData.fecha_inicio}
                                                    onChange={(date) => handleDatePicker('fecha_inicio', date)}
                                                    dateFormat="dd/MM/yyyy"
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                    autoComplete="off"
                                                />
                                                <label className="label-datepicker">Fecha inicio</label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3 form-group mb-3">
                                        <div className="input-group">
                                            <div className="form-floating">
                                                <DatePicker
                                                    className="form-control text-center input-datepicker"
                                                    selected={eventoData.fecha_fin}
                                                    onChange={(date) => handleDatePicker('fecha_fin', date)}
                                                    dateFormat="dd/MM/yyyy"
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                    autoComplete="off"
                                                />
                                                <label className="label-datepicker">Fecha fin</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-12 mt-3 d-flex justify-content-end">
                <button className="btn btn-secondary me-2" onClick={() => navigate('/eventos')} disabled={statusProcesar}>
                    Cancelar
                </button>
                {
                    statusProcesar
                        ? <button className="btn btn-success" disabled><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Guardando</button>
                        : <button className="btn btn-success" onClick={handleSubmit}><i className="fas fa-check me-1"></i> Guardar</button>
                }
            </div>
        </div>
    );
};

export default EditarEvento;
